/**
 * Member chip ("Status Circles" legend, 2026-08): avatar circle whose ONE
 * ring + corner decision badge come from the single pure
 * `deriveMemberChipVisual` (card-kit) — see the design matrix there.
 *   applicant/guarantor  initials; ring = workflow stage (gray/blue/amber/red/
 *                        green), same rule for both; ✓/✗ badge (circle for
 *                        applicant, shield for guarantor) on a decision
 *   child      green ring + face avatar (no screening state, no trail)
 *   invited    gray ring + initials (added, hasn't started; no trail)
 * The chip is surface-agnostic (dashboard cards, listing rows, pipeline).
 * DTO-only (DashboardMemberChipData); no fetching.
 */
import { Check, Shield, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import {
	MEMBER_TOOLTIP_CLASS,
	StepPillTrail,
} from '#app/components/screening/StepPillTrail'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import {
	type DashboardMemberChipData,
	MEMBER_CHIP_BADGE,
	MEMBER_CHIP_RING_CLASS,
	SCREENING_TYPE_LABEL,
	deriveMemberChipVisual,
} from '#app/utils/application-detail-v2/vendor/card-kit'
import { cn } from '#app/utils/misc'

/** sm = chip (28px), lg = sheet rows (36px), xl = the combined overview
 * tiles (64px, may carry the Veriff face photo). */
type AvatarSize = 'sm' | 'lg' | 'xl'

function DecisionBadge({
	badge,
	size = 'sm',
}: {
	badge: NonNullable<ReturnType<typeof deriveMemberChipVisual>['badge']>
	size?: AvatarSize
}) {
	const def = MEMBER_CHIP_BADGE[badge]
	const Mark = def.mark === 'check' ? Check : X
	if (def.shape === 'shield') {
		// Shield outline (guarantor decision) with the mark centered inside it.
		return (
			<span
				className={
					size === 'xl'
						? 'absolute -top-0.5 -right-0.5'
						: 'absolute -top-1 -right-1'
				}
			>
				<Shield
					className={cn(
						size === 'xl' ? 'size-6' : size === 'lg' ? 'size-5' : 'size-4',
						def.className,
					)}
					aria-hidden="true"
				/>
				<Mark
					className={cn(
						'absolute inset-0 m-auto stroke-[3]',
						size === 'xl' ? 'size-3' : size === 'lg' ? 'size-2.5' : 'size-2',
						def.className,
					)}
					aria-hidden="true"
				/>
			</span>
		)
	}
	// Circle (applicant decision): colored fill by decision — green for an
	// accept (check), red for a decline (x) — with a white border + mark.
	return (
		<span
			aria-hidden="true"
			className={cn(
				'absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full border-1 border-white text-white',
				def.mark === 'check' ? 'bg-[#16A34A]' : 'bg-[#DC2634]',
				size === 'xl' ? 'size-6' : size === 'lg' ? 'size-4' : 'size-3',
			)}
		>
			<Mark
				className={cn(
					'stroke-[3] text-white',
					size === 'xl' ? 'size-3.5' : size === 'lg' ? 'size-2.5' : 'size-2',
				)}
			/>
		</span>
	)
}

/** Ringed avatar + decision badge on its own — the chip's visual grammar at
 * any size, so larger surfaces (the All Applicants sheet rows, the
 * TimelinePanel's Select Applicant chips) can't drift from the compact
 * chips. Same single `deriveMemberChipVisual` source; the prop is the
 * minimal visual slice so non-dashboard DTOs can feed it too. */
export function MemberAvatar({
	member,
	size = 'sm',
	photoUrl,
}: {
	member: Pick<
		DashboardMemberChipData,
		| 'kind'
		| 'status'
		| 'hasStarted'
		| 'infoRequested'
		| 'startedAt'
		| 'billingHeld'
		| 'initials'
	>
	size?: AvatarSize
	/** Veriff face photo (combined overview tiles); initials when absent. */
	photoUrl?: string | null
}) {
	const visual = deriveMemberChipVisual(member)
	return (
		<span
			className={cn(
				'relative flex shrink-0 items-center justify-center rounded-full text-center font-normal text-[#32302F]',
				size === 'xl'
					? 'size-16 border-2 text-lg/none font-semibold'
					: size === 'lg'
						? 'size-9 border-1 text-sm/none'
						: 'size-7 border-1 text-[10px]/none',
				MEMBER_CHIP_RING_CLASS[visual.ringTone],
			)}
		>
			{visual.avatar === 'face' ? (
				<img
					src="/img/kid-smile.svg"
					alt=""
					className={
						size === 'xl' ? 'size-9' : size === 'lg' ? 'size-6' : 'size-4'
					}
					aria-hidden="true"
				/>
			) : photoUrl ? (
				<img
					src={photoUrl}
					alt=""
					className="size-full rounded-full object-cover"
					referrerPolicy="no-referrer"
				/>
			) : (
				member.initials
			)}
			{visual.badge ? <DecisionBadge badge={visual.badge} size={size} /> : null}
		</span>
	)
}

/** Ringed avatar + short name inside the chip's bordered pill — exported so
 * the report pages' applicant switcher renders the identical chip body
 * (client 2026-09-09) from the minimal visual slice + a name. */
export function MemberChipBody({
	member,
	size = 'sm',
}: {
	member: Pick<
		DashboardMemberChipData,
		| 'kind'
		| 'status'
		| 'hasStarted'
		| 'infoRequested'
		| 'startedAt'
		| 'billingHeld'
		| 'initials'
		| 'shortName'
	>
	size?: AvatarSize
}) {
	// min-w-0/max-w-full let the chip shrink INSIDE a narrow column instead of
	// overrunning it: on a 320px card the name truncates further rather than the
	// chip spilling past the card edge (QA mobile). Roomy columns are unchanged —
	// max-w-28 still caps the name there.
	return (
		<span
			className={cn(
				'inline-flex max-w-full min-w-0 items-center gap-2 rounded-full border border-[#F0F0F0] bg-[#FBFDFD] px-2.5',
				size === 'lg' ? 'py-2.5' : 'py-1',
			)}
		>
			<MemberAvatar member={member} size={size} />
			<span
				className={cn(
					'max-w-28 min-w-0 truncate text-[#32302F] px-1',
					size === 'lg' ? 'text-sm' : 'text-xs',
				)}
			>
				{member.shortName}
			</span>
		</span>
	)
}

/** Bottom-pointing arrow that reads as PART of the tooltip: a rotated square
 * sharing the tooltip's white fill + #F0F0F0 border, straddling the bottom edge
 * so the border flows into it seamlessly (Radix's SVG arrow can't carry a
 * border). The tooltip is fixed to side="top", so the arrow is always below. */
function TooltipBottomArrow() {
	return (
		<span
			aria-hidden="true"
			className="absolute bottom-0 left-1/2 size-3 -translate-x-1/2 translate-y-1/2 rotate-45 rounded-br-[3px] border-r border-b border-[#F0F0F0] bg-white"
		/>
	)
}

/** The member hover details — name, role, screening tier, step trail (or
 * "Invitation sent") — wrapped around ANY trigger. The compact chip and the
 * All Applicants sheet rows both render through this, so the tooltip can't
 * diverge between surfaces. Children (no screening state) get no tooltip;
 * `className` styles the trigger span (layout belongs to the caller). */
export function MemberHoverCard({
	member,
	className,
	children,
}: {
	member: DashboardMemberChipData
	className?: string
	children: ReactNode
}) {
	// Children carry no screening state → trigger renders untouched.
	if (member.kind === 'child') {
		return <span className={className}>{children}</span>
	}
	// Invited members have no screening trail yet — but the landlord still
	// wants to know the invite went out, so the hover carries a small
	// "Invitation sent" line instead of the step trail.
	if (member.kind === 'invited') {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span className={cn('cursor-pointer', className)}>{children}</span>
				</TooltipTrigger>
				<TooltipContent side="top" className={MEMBER_TOOLTIP_CLASS}>
					<p className="text-base font-semibold text-[#32302F]">
						{member.fullName}
						<span className="ml-2 text-xs font-normal text-[#747474]">
							{member.roleLabel}
						</span>
					</p>
					<p className="mt-1 text-sm text-[#747474]">Invitation sent</p>
					<TooltipBottomArrow />
				</TooltipContent>
			</Tooltip>
		)
	}
	// Applicant / guarantor → tooltip with name, role, the screening tier, and
	// (when present) the step trail. Shown even before any step exists so the
	// tier is visible on every surface this hover renders.
	const screening = member.screeningType
		? {
				label: SCREENING_TYPE_LABEL[member.screeningType],
				// Full = the app's green; pre-screening = a muted amber, so the
				// two tiers read apart at a glance.
				className:
					member.screeningType === 'full'
						? 'bg-[#F3F7EE] text-[#5C7C48]'
						: 'bg-[#FBF1E5] text-[#B4772A]',
			}
		: null
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				{/* span (not button): the hover is informative, the CARD navigates. */}
				<span className={cn('cursor-pointer', className)}>{children}</span>
			</TooltipTrigger>
			<TooltipContent side="top" className={MEMBER_TOOLTIP_CLASS}>
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
					<p className="text-base font-semibold text-[#32302F]">
						{member.fullName}
					</p>
					{/* Role + tier as compact pills so the header stays on one line
					    instead of the name being crowded by wide muted text. */}
					<span className="rounded-full bg-[#F4F4F2] px-2 py-0.5 text-[10px] font-semibold text-[#747474]">
						{member.roleLabel}
					</span>
					{screening ? (
						<span
							className={cn(
								'rounded-full px-2 py-0.5 text-[10px] font-semibold',
								screening.className,
							)}
						>
							{screening.label}
						</span>
					) : null}
				</div>
				{member.steps.length > 0 ? (
					<div className="mt-2">
						<StepPillTrail steps={member.steps} />
					</div>
				) : null}
				<TooltipBottomArrow />
			</TooltipContent>
		</Tooltip>
	)
}

export function MemberChip({
	member,
	size = 'sm',
	href,
}: {
	member: DashboardMemberChipData
	size?: AvatarSize
	/** Individual detail-page link (2026-08, card-kit's deriveMemberDetailHref)
	 * — applicant/guarantor chips only. Wrapping the chip in a real `<Link>`
	 * (an `<a>`) makes the card's own row-click guard
	 * (`closest('button, a, [role="menuitem"]')`, ApplicationGroupCard) skip
	 * its combined-report navigation for this click, same as the QR share
	 * button and kebab menu already do — no extra event handling needed here.
	 * undefined (child/invited chips) → plain chip, falls through to the
	 * card's own click. */
	href?: string
}) {
	const chip = (
		<MemberHoverCard member={member}>
			<MemberChipBody member={member} size={size} />
		</MemberHoverCard>
	)
	return href ? <Link to={href}>{chip}</Link> : chip
}
